// packages
import { Component, h } from 'preact';

// components
import CandidateRow from './components/CandidateRow';
import HeadshotCandidateRow from './components/HeadshotCandidateRow';
import Contest from './components/Contest';
import NewContestShowcase from './components/NewContestShowcase';
import FramesChild from './components/FramesChild';
import SeatPartyDivider from './components/SeatPartyDivider';
import { DEMOCRAT, NEUTRAL, OTHER } from './colors';

// internal
import getJSON from './utils/get-json';
import {
  determineDistrictTitle,
  lastUpdatedFormat,
} from './election-utils';
import { RESULTS_IN_USE,  visibleCandidatesCount } from './endpoints';
import raceCandidateColors from './election-candidate-colors';

import { LENGTH_OF_CHECK_INTERVAL } from './constants';

const partyRegex = /^(.*)-(rep|dem)$/;

const partyShortToLong = {
  'rep': 'Republican',
  'dem': 'Democrat'
};

class App extends Component<{ contests: [] }, {}> {
  constructor() {
    super();

    this.state = { contests: [] };

    this.fetchData = this.fetchData.bind(this);
  }

  fetchData() {
    const now = new Date();

    const selectedContests = this.props.selectedContests;

    const selectedOffices = selectedContests.map(contest => {
      const singlePartyMatch = contest.office.match(partyRegex);
      return singlePartyMatch ? singlePartyMatch[1] : contest.office;
    });

    const asOf = lastUpdatedFormat(now);

    getJSON(RESULTS_IN_USE).then(contests => {
      const activeContests = contests.filter(contest => selectedOffices.includes(contest.office_slug))
      this.setState({ contests: activeContests, asOf }, this.startTimer);
    });
  }

  startTimer() {
    this.timer = window.setTimeout(this.fetchData, LENGTH_OF_CHECK_INTERVAL);
  }

  clearTimer() {
    window.clearTimeout(this.timer);
  }

  componentDidMount() {
    this.fetchData();
  }

  componentWillUnmount() {
    this.clearTimer();
  }

  componentDidUpdate() {}

  renderOrderedComponents = () => {
    const { contests, asOf } = this.state;
    const { embedType, notes, annotations, selectedContests } = this.props;

    // We map over the params in the order they were selected
    return selectedContests.map((param, index) => {
      const { office, type } = param; // e.g., { office: 'governor-rep', type: 'contest' }

      if (type === 'contest') {
        let hideEmptyParty = false;
        let contestsToRender = [];
        const singlePartyMatch = office.match(partyRegex);

        // handle if a single party is specified
        if (singlePartyMatch) {
          const [_, officeSlug, partyShort] = singlePartyMatch;
          hideEmptyParty = true;
          const party = partyShortToLong[partyShort];
          contestsToRender = contests.filter(c => c.office_slug === officeSlug && c.party === party);
        } else {
          contestsToRender = contests.filter(c => c.office_slug === office);
        }

        if (!contestsToRender || contestsToRender.length === 0) return null;

        return (
          <Contest
            key={`contest-${office}-${index}`}
            contest={contestsToRender}
            hideEmptyParty={hideEmptyParty}
            columnCount={CandidateRow.columnCount}
            contestTitle={determineDistrictTitle(contestsToRender[0])}
            embedType={embedType}
            renderParty={(party) => this.renderPartySection(party, contestsToRender[0])}
          />
        );
      }

      if (type === 'map') {
        const singlePartyMatch = office.match(partyRegex);
        let contest = null;

        // handle if a single party is specified
        if (singlePartyMatch) {
          const [_, officeSlug, partyShort] = singlePartyMatch;
          const party = partyShortToLong[partyShort];
          contest = contests.find(c => c.office_slug === officeSlug && c.party === party);
        } else {
          return null;
        }

        if (!contest) return null;
        const contestIdentifier = `${contest.office_slug}-${contest.party}`
        const container = document.querySelector(`.${office}`);
        if (container) {
          container.parentElement.style.display = 'block';
        }

        // Logic for building a Map
        return (
          <NewContestShowcase
            contest={contest}
            showContestTitleInShowcase={true}
            container={container}
            notes={notes.filter(item => item.type === contestIdentifier)}
            annotations={annotations.filter(
              item => item.type === contestIdentifier
            )}
            asOf={asOf}
          />
        );
      }

      if (type === 'regions') {
        const singlePartyMatch = office.match(partyRegex);
        let contest = null;

        // handle if a single party is specified
        if (singlePartyMatch) {
          const [_, officeSlug, partyShort] = singlePartyMatch;
          const party = partyShortToLong[partyShort];
          contest = contests.find(c => c.office_slug === officeSlug && c.party === party);
        } else {
          return null;
        }

        if (!contest) return null;
        const contestIdentifier = `${contest.office_slug}-${contest.party}`;
        const container = document.querySelector(`.${office}-regions`);
        if (container) {
          container.parentElement.style.display = 'block';
        }

        // Logic for building regions bar charts only
        return (
          <NewContestShowcase
            contest={contest}
            showContestTitleInShowcase={true}
            container={container}
            notes={notes.filter(item => item.type === contestIdentifier)}
            annotations={annotations.filter(
              item => item.type === contestIdentifier
            )}
            asOf={asOf}
          />
        );
      }

      return null;
    });
  }

  renderPartySection = (party, parentContest) => {
    const { asOf } = this.state;
    const { annotations, notes, embedType } = this.props;

    party.Row = CandidateRow;

    const colorKey = `${party.office_slug}-${party.party}`;
    const candidateColorsList = raceCandidateColors[colorKey] || [];

    return (
      <div className="seat__race_wrapper" key={party.party}>
        <SeatPartyDivider
          partyName={party.party}
          partyBallot={party}
          tableHeader={HeadshotCandidateRow.Header}
          asOf={asOf}
          annotations={annotations.filter(item => item.type === `${party.office_slug}-${party.party}`)}
          notes={notes.filter(item => item.type === `${party.office_slug}-${party.party}`)}
          source={party.source}
          columnCount={party.Row.columnCount}
          contestTitle={determineDistrictTitle(parentContest)}
          embedType={embedType}
          renderCandidate={(candidate, idx, viewAll, { totalVotes }) => {
            const colorMatch = candidateColorsList.find(c => c.name === candidate.name);
            const candidateColor = colorMatch ? colorMatch.color : undefined;
            return (
              <party.Row
                key={candidate.id}
                ballotName={candidate.name}
                candidateId={candidate.id}
                contestIsCalled={party.is_called}
                contestResult={party.contest_result}
                candidatesNum={party.candidacies.length}
                isIncumbent={candidate.is_incumbent}
                isWinner={candidate.is_winner}
                isViewable={viewAll || idx < 4} // Assuming 4 for visibleCandidatesCount
                party={candidate.party}
                candidateColor={candidateColor}
                totalVotesInContest={totalVotes}
                votes={candidate.votes}
              />
            );
          }}
        />
      </div>
    );
  }

  render() {
    return (
      <FramesChild className="embed">
        {this.renderOrderedComponents()}
      </FramesChild>
    );
  }
}

export default App;
